import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

const DEBOUNCE_MS = 30_000 // 30 seconds
const COUNTDOWN_INTERVAL_MS = 1_000
const WARNING_LEAD_MINUTES = 5

/**
 * HIPAA § 164.312(a)(2)(iii): Client-side idle warning before server-side auto-logoff.
 *
 * Shows a countdown modal when the user has been idle for (timeout - 5) minutes.
 * User activity (and "Stay Signed In") POSTs keepalive; the local timer only
 * resets after the server accepts that refresh.
 */
export default class extends Controller {
  static values = {
    timeout: Number,   // total timeout in minutes (from org setting)
    keepaliveUrl: String,
  }

  static targets = ["modal", "title", "message", "stayButton"]

  declare timeoutValue: number
  declare keepaliveUrlValue: string
  declare modalTarget: HTMLDialogElement
  declare titleTarget: HTMLElement
  declare messageTarget: HTMLElement
  declare stayButtonTarget: HTMLButtonElement

  private warningTimer: ReturnType<typeof setTimeout> | null = null
  private countdownInterval: ReturnType<typeof setInterval> | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private lastActivity: number = Date.now()
  private boundResetIdle: () => void = () => {}
  private boundOnPageShow: (event: PageTransitionEvent) => void = () => {}
  private refreshInFlight = false

  connect() {
    this.boundResetIdle = this.debouncedResetIdle.bind(this)
    this.boundOnPageShow = this.onPageShow.bind(this)
    this.addActivityListeners()
    window.addEventListener("pageshow", this.boundOnPageShow)
    this.startWarningTimer()
    this.populateStaticText()
  }

  disconnect() {
    this.removeActivityListeners()
    window.removeEventListener("pageshow", this.boundOnPageShow)
    this.clearTimers()
  }

  // --- Activity tracking (debounced) ---

  private addActivityListeners() {
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"]
    events.forEach((event) => {
      document.addEventListener(event, this.boundResetIdle, { passive: true })
    })
  }

  private removeActivityListeners() {
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"]
    events.forEach((event) => {
      document.removeEventListener(event, this.boundResetIdle)
    })
  }

  private debouncedResetIdle() {
    if (this.debounceTimer) return
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
    }, DEBOUNCE_MS)

    void this.refreshSession()
  }

  // Turbo/bfcache restores can reconnect without a server round-trip.
  private onPageShow(event: PageTransitionEvent) {
    if (event.persisted) void this.refreshSession()
  }

  // --- Warning timer ---

  private get warningDelayMs(): number {
    const warningMinutes = Math.max(this.timeoutValue - WARNING_LEAD_MINUTES, 0)
    return warningMinutes * 60 * 1000
  }

  private startWarningTimer() {
    if (this.warningTimer) clearTimeout(this.warningTimer)
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    this.dismissModal()

    this.warningTimer = setTimeout(() => {
      this.showWarning()
    }, this.warningDelayMs)
  }

  // --- Warning modal ---

  private showWarning() {
    const expiresAt = this.lastActivity + this.timeoutValue * 60 * 1000
    this.updateCountdown(expiresAt)

    this.countdownInterval = setInterval(() => {
      this.updateCountdown(expiresAt)
    }, COUNTDOWN_INTERVAL_MS)

    this.modalTarget.showModal()
  }

  private updateCountdown(expiresAt: number) {
    const remaining = Math.max(0, expiresAt - Date.now())
    const totalSeconds = Math.ceil(remaining / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    this.messageTarget.textContent = i18n.t("session_idle.warning_message", {
      minutes: String(minutes),
      seconds: String(seconds).padStart(2, "0"),
    })

    if (remaining <= 0) {
      this.clearTimers()
      // Server will handle the actual logoff on next request; force a navigation
      window.location.reload()
    }
  }

  private populateStaticText() {
    this.titleTarget.textContent = i18n.t("session_idle.warning_title")
    this.stayButtonTarget.textContent = i18n.t("session_idle.stay_signed_in")
  }

  private dismissModal() {
    if (this.modalTarget.open) {
      this.modalTarget.close()
    }
  }

  // --- Stay signed in ---

  staySignedIn() {
    void this.refreshSession()
  }

  private async refreshSession() {
    if (this.refreshInFlight) return
    this.refreshInFlight = true

    try {
      const response = await fetch(this.keepaliveUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json",
        },
      })

      if (response.status === 401) {
        window.location.assign("/sign-in")
        return
      }

      if (!response.ok) return

      this.lastActivity = Date.now()
      this.startWarningTimer()
    } catch {
      // Leave local timers alone — server expiry still governs.
    } finally {
      this.refreshInFlight = false
    }
  }

  // --- Helpers ---

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content") || ""
  }

  private clearTimers() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer)
      this.warningTimer = null
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }
}
